import { ControlledTextField } from 'components/Form';
import { Button, IconCheck, IconClose } from 'uikit';

import type { CustomCellRendererProps } from 'ag-grid-react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import type { IRowWithCommonOptions } from '../types';

enum StateName {
    isEditInProgress = 'isEditInProgress',
    isFrozen = 'isFrozen',
}

type INameCellRenderer = CustomCellRendererProps<IRowWithCommonOptions> & {
    setValueState: (stateName: StateName, newState: boolean) => void;
};

export const NameCellRenderer = (props: INameCellRenderer) => {
    const { data, value, valueFormatted, api, setValueState } = props;

    const { control, reset, handleSubmit, setError, clearErrors } = useForm();

    useEffect(() => {
        if (value) {
            reset({
                name: value,
            });
        }
    }, [reset, value]);

    const saveEditing = useCallback(() => {
        handleSubmit((form) => {
            const newValue = form.name.trim();
            let hasCurrentValue = false;

            api?.forEachNode((node) => {
                const id = node?.data?.id;
                const nodeValue = node?.data?.name ?? '';

                if (id !== data?.id && nodeValue.toLowerCase() === newValue.toLowerCase()) {
                    hasCurrentValue = true;
                }
            });

            if (hasCurrentValue) {
                setError('name', { message: 'Работа с таким наименованием уже существует' });

                return;
            }

            if (newValue) {
                api?.applyTransaction({
                    update: [
                        {
                            ...data,
                            number: data?.number,
                            name: newValue,
                            isCreating: false,
                            isEditing: false,
                        },
                    ],
                });

                clearErrors('name');
                setValueState(StateName.isEditInProgress, false);
                setValueState(StateName.isFrozen, false);
            }
        })();
    }, [api, clearErrors, data, handleSubmit, setError, setValueState]);

    const cancelEditing = useCallback(() => {
        if (data?.isEditing) {
            api?.applyTransaction({
                update: [
                    {
                        ...data,
                        name: data.name,
                        number: data?.number,
                        isEditing: false,
                    },
                ],
            });

            reset({
                name: data.name,
            });
        }

        if (data?.isCreating) {
            api?.applyTransaction({
                remove: [data],
            });
        }

        setValueState(StateName.isEditInProgress, false);
    }, [api, data, reset, setValueState]);

    const rightSide = useCallback(
        () => (
            <>
                <Button iconLeft={IconCheck} onClick={saveEditing} view="clear" size="xs" onlyIcon />
                <Button iconLeft={IconClose} onClick={cancelEditing} view="clear" size="xs" onlyIcon />
            </>
        ),
        [cancelEditing, saveEditing],
    );

    if (data?.isCreating || data?.isEditing) {
        return <ControlledTextField control={control} name="name" rightSide={rightSide} autoFocus />;
    }

    return valueFormatted;
};

NameCellRenderer.displayName = 'NameCellRenderer';
